import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  PropertyService,
} from "@propertelligent/client-api";
import { useForm, FormProvider } from "react-hook-form";

import "./AddProperty.css";
import Card from "../card";
import InputField from "../form-fields/InputField";
import Uploader from "../CustomUploader/Uploader";
import {
  JPEG_PNG_EXTENSIONS,
  JPEG_PNG_MIME_TYPES,
} from "../CustomUploader/imageUploadConstants";
import Radio from "../form-fields/radio";
import AddressAutocompleteRHF from "../AutoComplete/AddressAutocompleteRHF";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import apiHelper from "../../utils/apiHelper";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import { ConfirmationPopup } from "../confirmation-popup/confirmation-popup-demo";
import RadioGroup from "../form-fields/RadioGroup";
import LeftArrowTopHeader from "../form-fields/LeftArrowTopHeader";
import SimpleSpinner from "../../components/loader";
import SimpleOutlineButton from "../buttons/Simple-Outline-button";
import { useFieldChangesSnapshot } from "../../hooks/useFieldChangesSnapshot";

interface ProprtyFormValues {
  propertyType: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  unitImage: File | null;
}

interface EditPropertyProps {
  propertyId?: number;
}

const EditProperty = ({ propertyId: propId }: EditPropertyProps) => {
  const params = useParams<{ propertyId?: string }>();
  const propertyId = propId ?? (params.propertyId ? Number(params.propertyId) : undefined);
  const methods = useForm<ProprtyFormValues>({
    mode: "onChange",
    defaultValues: {
      propertyType: "2",
      address: "",
      city: "",
      state: "",
      zip: "",
      // undefined, not null: RHF computes dirtyFields.unitImage by comparing the
      // current value against this default. A default of null would make a real
      // delete (Uploader's handleDelete sets the field to null) compare equal to the
      // default and silently clear dirtyFields.unitImage, making the delete branch in
      // onSubmit below unreachable. undefined never equals a real File or null.
      unitImage: undefined,
    },
  });

  const { formState: { isDirty, isValid, dirtyFields } } = methods;

  const { handleSubmit, setValue, control } = methods;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isUnsavedChangesOpen, setIsUnsavedChangesOpen] = useState(false);
  const [mainImage, setMainImage] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null); // Explicitly type documentId as number | null
  // This page is edit-only (propertyId always provided by its sole caller), so the
  // initial fetch always runs -- start loading so the form doesn't flash blank
  // defaultValues before getPropertyDetails() resolves.
  const [isPageLoading, setIsPageLoading] = useState(!!propertyId);
  // A failed load leaves the snapshot unset, which correctly keeps Save permanently
  // disabled -- but without this, the user just sees a normal-looking blank form with no
  // indication why Save never enables once the toast fades. Show an explicit retry state
  // instead of falling through to the (unsavable) form.
  const [loadError, setLoadError] = useState(false);

  // Snapshot of the loaded property's editable text/radio fields (unitImage is tracked
  // separately via dirtyFields.unitImage below). RHF's own isDirty proved unreliable as
  // the sole Save-enablement signal for this form in production -- Save could enable
  // with no actual user edit. hasFieldChanges is a plain value comparison against this
  // snapshot, ANDed with isDirty (not a replacement for it) so nothing regresses relative
  // to the app-wide isDirty && isValid convention -- it only narrows further.
  const { hasFieldChanges, setSnapshot } = useFieldChangesSnapshot<{
    address: string;
    city: string;
    state: string;
    zip: string;
    propertyType: string;
  }>(control, ["address", "city", "state", "zip", "propertyType"]);

  // Same "did anything actually change" signal Save is gated on -- reused here too so
  // Cancel's unsaved-changes prompt never fires for an edit-then-revert that Save itself
  // considers unchanged (e.g. user tweaks a field then types it back to its loaded value).
  const hasUnsavedChanges = (isDirty && hasFieldChanges) || Boolean(dirtyFields.unitImage);

  useEffect(() => {
    if (propertyId) {
      getPropertyDetails();
    }
  }, [propertyId]);

  const getPropertyDetails = async () => {
    // Also re-set on Retry (harmless on the initial call, where it's already true) --
    // without it, a Retry click transiently re-shows the blank unsavable form instead
    // of the loading overlay for the duration of the refetch.
    setIsPageLoading(true);
    setLoadError(false);
    try {
      const res = await apiHelper(
        PropertyService.getApiProperty1,
        { showNotification: false },
        propertyId
      );

      if (res?.data?.success) {
        const { propertyTypeId, mainImageBase64, streetAddress } = res.data.success;
        setValue("propertyType", propertyTypeId?.toString() || "2");
        setMainImage(res?.data?.success?.mainImageBase64 ? "data:image/*;base64," + res?.data?.success?.mainImageBase64 : null);
        setDocumentId(res?.data?.success?.mainImageId ?? null); // Set documentId or null if not present
        // shouldTouch surfaces InputField's error message immediately for any field
        // whose already-stored value fails validation (e.g. a legacy city/state value
        // containing a digit) -- InputField only renders an error once a field is
        // touched or the form is submitted, and a value set here was never blurred by
        // the user, so without this the field would silently keep Save disabled with no
        // visible explanation. shouldValidate also recomputes formState.isValid for the
        // whole form; without it, a property with no existing image never gets a
        // validation pass at all (Uploader's own defaultImage-init effect is otherwise
        // the only place that triggers one, and it only runs when there's an image).
        const loadedFieldOptions = { shouldTouch: true, shouldValidate: true };
        setValue("address", res?.data?.success?.streetAddress || "", loadedFieldOptions);
        setValue("city", res?.data?.success?.city || "", loadedFieldOptions);
        setValue("state", res?.data?.success?.state || "", loadedFieldOptions);
        setValue("zip", res?.data?.success?.zip || "", loadedFieldOptions);

        setSnapshot({
          address: res?.data?.success?.streetAddress || "",
          city: res?.data?.success?.city || "",
          state: res?.data?.success?.state || "",
          zip: res?.data?.success?.zip || "",
          propertyType: propertyTypeId?.toString() || "2",
        });
      } else {
        // Snapshot stays unset here, which permanently keeps hasFieldChanges (and
        // therefore Save) disabled. loadError swaps the form out for an explicit retry
        // state instead of leaving the user on a normal-looking but unsavable form.
        setLoadError(true);
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Failed to load property details",
          type: NotificationType.Failure,
        });
      }
    } catch (err) {
      console.error("Failed to fetch property details", err);
      setLoadError(true);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load property details",
        type: NotificationType.Failure,
      });
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleCancelAction = () => {
    setIsUnsavedChangesOpen(false);
  };

  const handleContinueWithoutSaving = () => {
    setIsUnsavedChangesOpen(false);
    navigate(-1);
  };

  const onSubmit = async (data: ProprtyFormValues) => {
    try {
      setLoading(true);

      const propertyObject = {
        id: propertyId,
        address: {
          streetAddress: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
        },
        propertyTypeId: Number(data.propertyType),
      };

      // Update property details
      const response = await apiHelper(PropertyService.putApiProperty,
        {
          showNotification: true,
          successMessage: "Property Updated Successfully",
        },
        propertyId,
        propertyObject
      );

      if (response?.data?.success) {
        // Only touch the image endpoints when the user actually interacted with the
        // uploader this session -- data.unitImage is otherwise always truthy, since
        // Uploader's defaultImage init effect reconstructs the existing photo into a
        // File via setValue without shouldDirty, so dirtyFields.unitImage is the only
        // reliable signal that the image itself changed.
        if (dirtyFields.unitImage && data.unitImage) {
          const imgData = {
            EntityTypeId: DocumentEntityTypesEnum.Property,
            EntityId: propertyId,
            DocumentTypeId: DocumentTypesEnum.BeforeImage,
            IsMain: true,
            Document: data.unitImage,
            FileName: data.unitImage.name,
          };

          await apiHelper(
            DocumentService.postApiDocument,
            {
              successMessage: "Property Image Uploaded Successfully",
              showNotification: true,
            },
            imgData
          );
        }
        // Handle image deletion if image is removed and documentId exists
        else if (dirtyFields.unitImage && !data.unitImage && documentId) {
          await apiHelper(
            DocumentService.deleteApiDocument,
            {
              successMessage: "Property Image Deleted Successfully",
              showNotification: true,
            },
            documentId
          );
          setDocumentId(null); // Clear documentId after deletion
          setMainImage(null); // Clear mainImage after deletion
        }

        navigate(-1);
      } else if (response?.errors?.Address) {
        PubSub.publish(TopicsEnum.Notification.toString(), {
          message: "Property Already Exists",
          type: NotificationType.Failure,
        });
      }
    } catch (error) {
      console.error("Submission failed", error);
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to update property",
        type: NotificationType.Failure,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="d-flex flex-column gap-1 full-page-content edit-property-page">
        <LeftArrowTopHeader className="mt-2" title="Edit Property" onCancel={() => { if (hasUnsavedChanges) setIsUnsavedChangesOpen(true); else navigate(-1); }} />
        <Card className="mt-0">
          <div style={{ position: "relative" }}>
            {isPageLoading && (
              // The form below stays mounted (not conditionally unmounted) while this
              // spinner shows -- InputField/Radio register their RHF validation rules on
              // mount, and getPropertyDetails()'s shouldValidate:true setValue calls need
              // those rules already registered to actually validate against. Unmounting
              // the form until load finishes silently no-ops that validation pass
              // (confirmed by EditProperty.test.tsx's pre-existing-invalid-city regression
              // test failing under that approach). Kept scoped to this card (not a
              // full-viewport overlay like LoaderOverlay) -- just a plain spinner, per
              // request, not a boxed "Loading..." message.
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ position: "absolute", inset: 0, zIndex: 1, background: "var(--bg-secondary)" }}
              >
                <SimpleSpinner myClassName="Icon50" />
              </div>
            )}
            {!isPageLoading && loadError && (
              <div className="d-flex flex-column align-items-center gap-3 py-5">
                <p className="normal_text">Failed to load property details.</p>
                <SimpleOutlineButton label="Retry" onClick={getPropertyDetails} />
              </div>
            )}
          <div
            className="row"
            style={
              loadError
                ? { visibility: "hidden", position: "absolute" }
                : isPageLoading
                  ? { visibility: "hidden" }
                  : undefined
            }
          >
            <div className="col-md-6">
              <div className="add-property-form-inner">
                <FormProvider {...methods}>
                  <form id="unitForm" onSubmit={handleSubmit(onSubmit)}>
                    <div className="row">
                      <div className="col-md-12 d-flex align-items-center gap-3">
                        <RadioGroup
                          label="Property Type"
                          required
                          requiredMessage="Please select property type"
                        >
                          <Radio
                            name="propertyType"
                            value="1"
                            label="Single Family"
                          />
                          <Radio
                            name="propertyType"
                            value="2"
                            label="Multi Family"
                          />
                        </RadioGroup>
                      </div>


                      <div className="col-md-12">
                        <Uploader
                          name="unitImage"
                          allowedFormats={[...JPEG_PNG_MIME_TYPES]}
                          allowedExtensions={[...JPEG_PNG_EXTENSIONS]}
                          maxSizeMB={2}
                          defaultImage={mainImage}
                        />
                      </div>

                      {/* <div className="col-md-12">
                        <AddressAutocompleteRHF />
                      </div> */}
                      <div className="col-md-12">
                        <InputField
                          name="address"
                          label="Street Address"
                          required
                          requiredMessage="Street Address is required"
                          placeholder="Enter Address"
                          validation={{
                            maxLength: {
                              value: 100,
                              message:
                                "Street Address cannot exceed 100 characters",
                            },
                          }}
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="city"
                          label="City"
                          required
                          requiredMessage="City is required"
                          placeholder="Enter City"
                          validation={{
                            maxLength: {
                              value: 70,
                              message: "City cannot exceed 70 characters",
                            },
                            pattern: {
                              value: /^[A-Za-z\s]+$/,
                              message: "City must contain only letters",
                            },
                          }}
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="state"
                          label="State"
                          required
                          placeholder="Enter State"
                          requiredMessage="State is required"
                          uppercase
                          validation={{
                            pattern: {
                              value: /^[A-Z]{2}$/,
                              message: "Please enter the state as its 2-letter code",
                            },
                          }}
                        />
                      </div>

                      <div className="col-md-4">
                        <InputField
                          name="zip"
                          label="Zip Code"
                          required
                          placeholder="Enter Zip Code"
                          requiredMessage="Zip Code is required"
                          validation={{
                            pattern: {
                              value: /^\d{5}$/,
                              message: "Zip code must be 5 digits",
                            },
                          }}
                        />
                      </div>
                    </div>
                  </form>
                </FormProvider>
              </div>
            </div>
          </div>
          </div>
        </Card>
      </div>

      <FixedFooter
        onCancel={() => hasUnsavedChanges ? setIsUnsavedChangesOpen(true) : navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        loading={loading}
        buttonLabel={"Save"}
        isSubmitDisabled={loading || !isValid || !hasUnsavedChanges}
      />
      <ConfirmationPopup
        isOpen={isUnsavedChangesOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. If you go back or exit this step, your changes will be lost. Do you want to continue?"
        primaryButtonLabel="Cancel"
        secondaryButtonLabel="Continue Without Saving"
        onPrimaryAction={handleCancelAction}
        onSecondaryAction={handleContinueWithoutSaving}
        onClose={handleCancelAction}
        isPrimaryAction={false}
      />
    </>
  );
};

export default EditProperty;